/* ============================================================
   HYDRAW — global utilities
   - reveal-on-scroll IntersectionObserver
   - sticky header shadow on scroll
   - smooth-scroll links with header-offset
   - publish-subscribe for section-level scroll triggers
   ============================================================ */

(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal on scroll ---------- */
  const revealEls = document.querySelectorAll('.reveal');
  if ('IntersectionObserver' in window && revealEls.length) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    revealEls.forEach((el) => io.observe(el));
  } else {
    revealEls.forEach((el) => el.classList.add('is-visible'));
  }

  /* ---------- Header scrolled state ---------- */
  const header = document.querySelector('[data-site-header]');
  if (header) {
    const SCROLL_TRIGGER = 12;
    const onScroll = () => {
      if (window.scrollY > SCROLL_TRIGGER) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Smooth scroll with header offset for in-page anchors ---------- */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    const id = link.getAttribute('href');
    if (!id || id === '#' || id.length < 2) return;
    const target = document.querySelector(id);
    if (!target) return;
    e.preventDefault();
    const headerH = header ? header.getBoundingClientRect().height : 0;
    const top = target.getBoundingClientRect().top + window.scrollY - headerH - 8;
    window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
    history.pushState(null, '', id);
  });

  /* ---------- Smart "Get HYDRAW" CTA on product page
     If we're on the product template, the CTA scrolls up to the buy block (#buy)
     instead of navigating away. The scrolling logic is inside the click handler above
     when the CTA is rendered with href="#buy"; on non-product pages the CTA is
     rendered with the product URL instead (handled in section header.liquid). */

  /* ---------- Section-level event hub
     Sections can subscribe to a single rAF-throttled scroll loop instead of each
     attaching their own listener — keeps frame budget tight. */
  const subs = new Set();
  let ticking = false;
  function tick() {
    const y = window.scrollY;
    const h = window.innerHeight;
    subs.forEach((fn) => { try { fn(y, h); } catch (e) { /* swallow per-sub error */ } });
    ticking = false;
  }
  window.addEventListener('scroll', () => {
    if (!ticking) { ticking = true; window.requestAnimationFrame(tick); }
  }, { passive: true });
  window.addEventListener('resize', () => {
    if (!ticking) { ticking = true; window.requestAnimationFrame(tick); }
  }, { passive: true });

  window.HYDRAW = window.HYDRAW || {};
  window.HYDRAW.onScroll = (fn) => { subs.add(fn); fn(window.scrollY, window.innerHeight); return () => subs.delete(fn); };
  window.HYDRAW.reduceMotion = reduceMotion;

  /* ---------- Klaviyo "Added to Cart" tracking ----------
     Listens for the theme's `cart:added` custom event (fired by PDP + drawer
     upsell). e.detail.items = the response from /cart/add.js (line items that
     were just added). We fetch the live cart for the running total + full
     items list, then push the event via whichever Klaviyo global is loaded
     (klaviyo.js exposes `klaviyo`, the legacy snippet uses `_learnq`). */
  function klaviyoTrack(name, props) {
    try {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['track', name, props]);
      } else if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['track', name, props]);
      }
    } catch (e) { /* never let analytics break checkout */ }
  }

  document.addEventListener('cart:added', (e) => {
    const added = (e && e.detail && e.detail.items) || [];
    fetch('/cart.js', { cache: 'no-store' })
      .then((r) => r.json())
      .then((cart) => {
        const checkoutUrl = window.location.origin + '/checkout';
        const lineList = (added.length ? added : cart.items).map((it) => ({
          ProductID: it.product_id,
          SKU: it.sku,
          ProductName: it.product_title || it.title,
          Quantity: it.quantity,
          ItemPrice: (it.price || 0) / 100,
          RowTotal: ((it.final_line_price || it.line_price || 0)) / 100,
          ProductURL: window.location.origin + (it.url || ''),
          ImageURL: it.image || it.featured_image || ''
        }));
        const first = lineList[0] || {};
        klaviyoTrack('Added to Cart', {
          $value: (cart.total_price || 0) / 100,
          AddedItemProductName: first.ProductName,
          AddedItemProductID: first.ProductID,
          AddedItemSKU: first.SKU,
          AddedItemQuantity: first.Quantity,
          AddedItemPrice: first.ItemPrice,
          AddedItemImageURL: first.ImageURL,
          AddedItemURL: first.ProductURL,
          ItemNames: cart.items.map((i) => i.product_title || i.title),
          CheckoutURL: checkoutUrl,
          Items: cart.items.map((it) => ({
            ProductID: it.product_id,
            SKU: it.sku,
            ProductName: it.product_title || it.title,
            Quantity: it.quantity,
            ItemPrice: (it.price || 0) / 100,
            RowTotal: ((it.final_line_price || it.line_price || 0)) / 100,
            ProductURL: window.location.origin + (it.url || ''),
            ImageURL: it.image || it.featured_image || ''
          }))
        });
      })
      .catch(() => { /* noop */ });
  });
})();
